import React, { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { Logo, WordmarkTagline } from "@/components/Logo";
import { Building2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

// Static content + simple contact form (logs to console; would integrate with email provider in real run)
export function About() {
  return (
    <PublicLayout>
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="label-eyebrow text-secondary mb-2">About Vendora</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">A commerce platform for British sellers.</h1>
        <p className="mt-6 text-muted-foreground leading-relaxed">
          Vendora was built by operators tired of stitching together Shopify, Klaviyo, Xero, a courier portal, payment processors and ten Zapier workflows just to run a profitable online shop in the UK. We started with a simple question: what if all of that lived in one place, with one ledger, one identity, one source of truth?
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The result is a Commerce Operating System: not just a checkout, not just a marketplace, but the full stack from sourcing to scaling. Vendora is centralised where it should be (payments, ledger, fraud) and modular where it must be (storefronts, fulfilment, marketing).
        </p>
        <div className="mt-12 grid md:grid-cols-3 gap-3">
          {[
            { v: "2026", l: "Founded" },
            { v: "UK", l: "Where we sell" },
            { v: "GBP", l: "Priced in pounds, VAT included" },
          ].map((s) => (
            <div key={s.l} className="v-card">
              <div className="font-display font-black text-3xl text-primary">{s.v}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="mt-12"><WordmarkTagline /></div>
      </section>
    </PublicLayout>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Message received. We'll reply within 4 business hours.");
    setForm({ name: "", email: "", subject: "", message: "" });
  };
  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="label-eyebrow text-secondary mb-2">Contact</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">Talk to a human.</h1>
          <p className="mt-4 text-muted-foreground">For partnerships, technical questions, or anything sales can help with.</p>
          <div className="mt-8 space-y-4">
            <Detail icon={Mail} text="hello@vendora.ca" />
            <Detail icon={MapPin} text="Serving sellers across the United Kingdom" />
            <Detail icon={Building2} text="Vendora" />
          </div>
        </div>
        <form onSubmit={submit} className="v-card">
          <Field label="Name" v={form.name} onChange={(v) => setForm({...form, name: v})} testid="contact-name" required />
          <Field label="Email" type="email" v={form.email} onChange={(v) => setForm({...form, email: v})} testid="contact-email" required />
          <Field label="Subject" v={form.subject} onChange={(v) => setForm({...form, subject: v})} testid="contact-subject" required />
          <div>
            <label className="label-eyebrow mb-2 block">Message</label>
            <textarea rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required className="v-input" data-testid="contact-message" />
          </div>
          <button className="v-btn-primary mt-5 w-full" data-testid="contact-submit">Send message</button>
        </form>
      </section>
    </PublicLayout>
  );
}

function Detail({ icon: Icon, text }) {
  return <div className="flex items-center gap-3 text-sm"><Icon size={16} className="text-secondary" /> {text}</div>;
}
function Field({ label, type = "text", v, onChange, testid, required }) {
  return (
    <div className="mb-4">
      <label className="label-eyebrow mb-2 block">{label}</label>
      <input type={type} value={v} onChange={(e) => onChange(e.target.value)} required={required} className="v-input" data-testid={testid} />
    </div>
  );
}
